import { useEffect } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { ShopFilterPanel } from './ShopFilterPanel'
import { StorefrontBackdrop } from '../../../components/storefront/chrome'
import type { ShopProductsFilter } from '../../../../viewmodels/useShopProducts'

/**
 * ShopFilterDrawer — left-anchored slide-OVER for the shop filter
 * panel. Mirrors the OperatorSidebar UX exactly (Kiran 2026-06-05):
 * StorefrontBackdrop fills the outer aside, then an inset walnut
 * panel (`absolute inset-3 ... rounded-2xl bg-gradient-to-b
 * from-bg-dark to-charcoal text-text-light shadow-lg`) carries the
 * actual chrome. Same close behavior as the cart drawer
 * (Esc / × / click-backdrop), same body scroll lock.
 *
 * The actual filter controls (price slider, categories, CDTs) live in
 * <ShopFilterPanel /> — this file is the chrome only.
 */
interface Props {
  readonly filter: ShopProductsFilter
  readonly onFiltersChange: (next: ShopProductsFilter) => void
  readonly onClose: () => void
}

export function ShopFilterDrawer({ filter, onFiltersChange, onClose }: Props): JSX.Element {
  const reduce = useReducedMotion() === true

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])

  return (
    <motion.div
      className="fixed inset-0 z-50 flex justify-start bg-charcoal/40"
      role="dialog"
      aria-modal="true"
      aria-label="Filters"
      onClick={onClose}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
      transition={{ duration: 0.4 }}
    >
      <motion.aside
        className="relative h-full w-72 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        variants={{ hidden: { x: reduce ? 0 : '-100%' }, visible: { x: 0 } }}
        transition={{ duration: reduce ? 0.2 : 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <StorefrontBackdrop />

        {/* Walnut panel — same recipe as OperatorSidebar: inset 3 from
            the outer aside so the cream backdrop frames it, rounded-2xl
            corners, vertical gradient from bg-dark to charcoal, light
            text. */}
        <div className="absolute inset-3 z-10 overflow-hidden rounded-2xl bg-gradient-to-b from-bg-dark to-charcoal text-text-light shadow-lg">
          {/* Close button — pinned to the outer edge of the panel (right
              side for a left-anchored drawer). Restyled for the dark
              walnut surface: translucent light fill, light outline. */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-text-light/20 bg-text-light/10 text-text-light backdrop-blur-md transition hover:bg-text-light/20"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          <div className="flex h-full flex-col overflow-y-auto px-5 py-6">
            <header className="mb-4 flex items-center justify-between gap-3">
              <h2 className="font-heading text-h3 text-text-light">Filters</h2>
            </header>

            <div className="h-px w-full bg-text-light/15" />

            <div className="mt-5 flex-1">
              <ShopFilterPanel filter={filter} onFiltersChange={onFiltersChange} />
            </div>
          </div>
        </div>
      </motion.aside>
    </motion.div>
  )
}
